// src/pages/mock-data-management-dashboard/components/MockDataPreview.jsx
import React, { useState, useMemo } from 'react';
import Icon from 'components/AppIcon';
import { format } from 'date-fns';

const MockDataPreview = ({ entityType, data, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const getTableColumns = (type) => {
    const columnConfig = {
      users: [
        { key: 'id', label: 'User ID', sortable: true },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'role', label: 'Role', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'createdAt', label: 'Created', sortable: true, type: 'date' },
        { key: 'lastLogin', label: 'Last Login', sortable: true, type: 'date' }
      ],
      biodataRecords: [
        { key: 'workerId', label: 'Worker ID', sortable: true },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'nationality', label: 'Nationality', sortable: true },
        { key: 'skills', label: 'Skills', sortable: false, type: 'array' },
        { key: 'experience', label: 'Experience', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'completionScore', label: 'Completion', sortable: true, type: 'percentage' }
      ],
      documents: [
        { key: 'id', label: 'Document ID', sortable: true },
        { key: 'workerId', label: 'Worker ID', sortable: true },
        { key: 'type', label: 'Type', sortable: true },
        { key: 'filename', label: 'Filename', sortable: true },
        { key: 'size', label: 'Size', sortable: true, type: 'filesize' },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'uploadedAt', label: 'Uploaded', sortable: true, type: 'date' },
        { key: 'expiryDate', label: 'Expires', sortable: true, type: 'date' }
      ],
      systemLogs: [
        { key: 'id', label: 'Log ID', sortable: true },
        { key: 'timestamp', label: 'Timestamp', sortable: true, type: 'datetime' },
        { key: 'level', label: 'Level', sortable: true },
        { key: 'action', label: 'Action', sortable: true },
        { key: 'entity', label: 'Entity', sortable: true },
        { key: 'userId', label: 'User ID', sortable: true },
        { key: 'message', label: 'Message', sortable: false },
        { key: 'ipAddress', label: 'IP Address', sortable: true }
      ]
    };
    return columnConfig[type] || [];
  };

  const formatCellValue = (value, column) => {
    if (value === null || value === undefined) return '-';

    switch (column.type) {
      case 'date':
        return format(new Date(value), 'MMM dd, yyyy');
      case 'datetime':
        return format(new Date(value), 'MMM dd, yyyy HH:mm');
      case 'array':
        return Array.isArray(value) ? value.join(', ') : value;
      case 'filesize':
        return formatFileSize(value);
      case 'percentage':
        return `${value}%`;
      default:
        return value;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status, entityType) => {
    const statusColorMap = {
      users: {
        active: 'text-success-600 bg-success-50',
        inactive: 'text-secondary-600 bg-secondary-50',
        pending: 'text-warning-600 bg-warning-50'
      },
      biodataRecords: {
        approved: 'text-success-600 bg-success-50',
        pending: 'text-warning-600 bg-warning-50',
        rejected: 'text-error-600 bg-error-50',
        under_review: 'text-accent-600 bg-accent-50'
      },
      documents: {
        verified: 'text-success-600 bg-success-50',
        pending: 'text-warning-600 bg-warning-50',
        rejected: 'text-error-600 bg-error-50',
        expired: 'text-secondary-600 bg-secondary-50'
      },
      systemLogs: {
        INFO: 'text-accent-600 bg-accent-50',
        WARN: 'text-warning-600 bg-warning-50',
        ERROR: 'text-error-600 bg-error-50',
        DEBUG: 'text-secondary-600 bg-secondary-50'
      }
    };
    return statusColorMap[entityType]?.[status] || 'text-secondary-600 bg-secondary-50';
  };

  const filteredAndSortedData = useMemo(() => {
    let filtered = data;

    // Apply search filter
    if (searchTerm) {
      filtered = data.filter(item =>
        Object.values(item).some(value =>
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle dates
        if (aValue instanceof Date) aValue = aValue.getTime();
        if (bValue instanceof Date) bValue = bValue.getTime();
        if (typeof aValue === 'string' && aValue.match(/^\d{4}-\d{2}-\d{2}/)) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [data, searchTerm, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredAndSortedData.slice(startIndex, startIndex + pageSize);
  }, [filteredAndSortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const columns = getTableColumns(entityType);

  return (
    <div className="card">
      {/* Header */}
      <div className="p-6 border-b border-secondary-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Data Preview</h3>
            <p className="text-sm text-text-secondary">
              {filteredAndSortedData.length} of {data.length} records
            </p>
          </div>
          <button
            onClick={onRefresh}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <Icon name="RefreshCw" size={16} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon name="Search" size={16} className="text-secondary-400" />
          </div>
          <input
            type="text"
            placeholder="Search data..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10 w-full max-w-md"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {paginatedData.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="Database" size={48} className="text-secondary-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-text-primary mb-2">No Data Available</h3>
            <p className="text-text-secondary mb-4">
              {searchTerm ? 'No records match your search criteria.' : 'Generate some data to get started.'}
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider ${
                      column.sortable ? 'cursor-pointer hover:bg-secondary-100' : ''
                    }`}
                    onClick={column.sortable ? () => handleSort(column.key) : undefined}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{column.label}</span>
                      {column.sortable && (
                        <Icon
                          name={
                            sortConfig.key === column.key
                              ? sortConfig.direction === 'asc' ?'ChevronUp' :'ChevronDown' :'ChevronsUpDown'
                          }
                          size={14}
                          className="text-secondary-400"
                        />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {paginatedData.map((item, index) => (
                <tr key={item.id || index} className="hover:bg-secondary-50">
                  {columns.map((column) => (
                    <td key={column.key} className="px-6 py-4 whitespace-nowrap text-sm">
                      {column.key === 'status' || column.key === 'level' ? (
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          getStatusColor(item[column.key], entityType)
                        }`}>
                          {item[column.key]}
                        </span>
                      ) : (
                        <span className="text-text-primary">
                          {formatCellValue(item[column.key], column)}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-secondary-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-text-secondary">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredAndSortedData.length)} of {filteredAndSortedData.length} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="ChevronLeft" size={14} />
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`btn btn-sm ${
                      currentPage === pageNum ? 'btn-primary' : 'btn-secondary'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="ChevronRight" size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MockDataPreview;